// src/components/common/chartZoom.tsx
//
// Reusable "click-to-mark then zoom" interaction for the Recharts time-series
// charts, replacing the per-chart <Brush> slider. Charts call getClickedTMs()
// in their onClick, render renderMarkers() as children (marker lines + shaded
// band), and overlay <ChartBandZoomButton/> (an absolutely-positioned HTML
// button centered on the band — reliable across all chart types). The parent
// dashboard renders one <ChartZoomToolbar/> for Clear / Reset.
import React from "react";
import { ReferenceLine, ReferenceArea } from "recharts";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export const MARKER_COLOR = "#d32f2f";
export const BAND_COLOR = "#1976d2";

// Chart left/right plot margins (must match the LineChart `margin` on the charts)
// so the overlay button lands on the correct x within the plot area.
export const PLOT_LEFT = 60;
export const PLOT_RIGHT = 8;

/** Extract the clicked time (epoch ms) from a Recharts chart onClick state. */
export function getClickedTMs(state: any): number | null {
  const n = Number(state?.activeLabel);
  return Number.isFinite(n) ? n : null;
}

const TZ = "America/Chicago";

/** Full "Jul 25, 18:47" (Nashville) — used for tooltips and marker labels. */
export const fmtDateTime = (ms: number) =>
  Number.isFinite(ms)
    ? new Date(ms).toLocaleString("en-US", {
        timeZone: TZ,
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";

/**
 * Adaptive X-axis tick formatter (Nashville time). Shows the date when the span
 * warrants it so multi-day views are unambiguous:
 *   span ≥ 3d  → "Jul 25"
 *   span ≥ 1d  → "7/25, 18:00"
 *   else       → "18:00"
 */
export function axisTickFormatter(domain: [number, number] | null) {
  const span = domain ? domain[1] - domain[0] : 0;
  return (v: any) => {
    const ms = Number(v);
    if (!Number.isFinite(ms)) return "";
    const d = new Date(ms);
    if (span >= 3 * 86_400_000)
      return d.toLocaleDateString("en-US", {
        timeZone: TZ,
        month: "short",
        day: "numeric",
      });
    if (span >= 86_400_000)
      return d.toLocaleString("en-US", {
        timeZone: TZ,
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    return d.toLocaleTimeString("en-US", {
      timeZone: TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };
}

/** Human duration between two epoch-ms marks, e.g. "42m", "3h 10m", "2d 4h". */
export function fmtDuration(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
}

/**
 * Marker overlay for a time chart, returned as an array of chart children
 * (Recharts flattens arrays of ReferenceLine/ReferenceArea children fine).
 * 0 markers → nothing; 1 → a single line; 2 → both lines + a shaded band.
 */
export function renderMarkers(markers: number[]): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  if (markers.length === 2) {
    const [a, b] = [...markers].sort((x, y) => x - y);
    nodes.push(
      <ReferenceArea
        key="zoom-band"
        x1={a}
        x2={b}
        fill={BAND_COLOR}
        fillOpacity={0.1}
        ifOverflow="hidden"
      />,
    );
  }
  markers.forEach((m, i) => {
    nodes.push(
      <ReferenceLine
        key={`zoom-mk-${i}`}
        x={m}
        stroke={MARKER_COLOR}
        strokeDasharray="4 3"
        strokeWidth={2}
        ifOverflow="hidden"
      />,
    );
  });
  return nodes;
}

/**
 * "Zoom in" button rendered as an HTML overlay centered over the selected band.
 * Positioned by fraction of the domain across the plot area (between the L/R
 * margins), so it works identically on every chart type. Render inside a
 * position:relative Box that also wraps the ResponsiveContainer.
 */
export const ChartBandZoomButton: React.FC<{
  domain: [number, number] | null;
  markers: number[];
  onZoom?: () => void;
  leftMargin?: number;
  rightMargin?: number;
}> = ({
  domain,
  markers,
  onZoom,
  leftMargin = PLOT_LEFT,
  rightMargin = PLOT_RIGHT,
}) => {
  if (!onZoom || markers.length !== 2 || !domain) return null;
  const span = domain[1] - domain[0];
  if (!(span > 0)) return null;
  const [a, b] = [...markers].sort((x, y) => x - y);
  const mid = (a + b) / 2;
  const frac = Math.max(0, Math.min(1, (mid - domain[0]) / span));
  return (
    <Button
      size="small"
      variant="contained"
      startIcon={<ZoomInIcon />}
      onClick={onZoom}
      sx={{
        position: "absolute",
        top: 8,
        left: `calc(${leftMargin}px + ${frac} * (100% - ${leftMargin + rightMargin}px))`,
        transform: "translateX(-50%)",
        zIndex: 5,
        py: 0.25,
        px: 1,
        minWidth: 0,
        fontSize: 12,
        textTransform: "none",
        boxShadow: 2,
        "& .MuiButton-startIcon": { mr: 0.5 },
      }}
    >
      Zoom in
    </Button>
  );
};

/**
 * Compact tooltip: small font, 2 columns when there are many series (e.g. the
 * 20 temperature zones), so it stays out of the way and fits everything.
 */
export function compactTooltip(labelFmt?: (v: any) => string) {
  return (props: any) => {
    const { active, payload, label } = props;
    if (!active || !payload?.length) return null;
    const twoCol = payload.length > 8;
    return (
      <Box
        sx={{
          bgcolor: "rgba(255,255,255,0.97)",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.5,
          fontSize: 11,
          lineHeight: 1.3,
          boxShadow: 3,
          maxWidth: 340,
        }}
      >
        <Box sx={{ fontWeight: 700, mb: 0.25 }}>
          {labelFmt ? labelFmt(label) : label}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: twoCol ? "1fr 1fr" : "1fr",
            columnGap: 1.5,
          }}
        >
          {payload.map((p: any, i: number) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: p.color,
                  flex: "0 0 auto",
                }}
              />
              <Box
                component="span"
                sx={{
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.name}
              </Box>
              <Box component="span" sx={{ ml: "auto", fontWeight: 700 }}>
                {typeof p.value === "number" ? p.value.toFixed(1) : p.value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };
}

type ToolbarProps = {
  markers: number[];
  zoomed: boolean;
  onClearMarkers: () => void;
  onResetZoom: () => void;
  /** Formats a marker's epoch-ms for display. Defaults to Nashville date-time
   *  (correct for true-epoch charts like Foam); pages whose timestamps are in a
   *  different space (e.g. Lam1/Lam2) pass their own. */
  formatTime?: (ms: number) => string;
};

export const ChartZoomToolbar: React.FC<ToolbarProps> = ({
  markers,
  zoomed,
  onClearMarkers,
  onResetZoom,
  formatTime,
}) => {
  const fmt = formatTime ?? fmtDateTime;
  const sorted = [...markers].sort((a, b) => a - b);
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {sorted.length === 2 ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Chip size="small" color="primary" label={fmt(sorted[0])} />
            <Typography variant="body2" color="text.secondary">
              →
            </Typography>
            <Chip size="small" color="primary" label={fmt(sorted[1])} />
            <Typography variant="body2" color="text.secondary">
              · {fmtDuration(sorted[1] - sorted[0])} · click “Zoom in” on the band
            </Typography>
          </Stack>
        ) : sorted.length === 1 ? (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={fmt(sorted[0])}
            />
            <Typography variant="body2" color="text.secondary">
              Click again to set the end marker.
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click a chart to place two markers, then zoom to that range.
          </Typography>
        )}
      </Box>
      <Button
        size="small"
        startIcon={<ClearAllIcon />}
        onClick={onClearMarkers}
        disabled={sorted.length === 0}
      >
        Clear markers
      </Button>
      <Button
        size="small"
        startIcon={<RestartAltIcon />}
        onClick={onResetZoom}
        disabled={!zoomed}
      >
        Reset zoom
      </Button>
    </Stack>
  );
};
